import { useEffect, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, FileText, FolderOpen, Upload } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { listBuildings } from "@/api/buildings";
import { DOCUMENTS_QUERY_KEY, listDocuments, uploadDocument, downloadDocument, changeDocumentVisibility, type CommunityDocument, type DocumentCategory, type DocumentVisibility } from "@/api/files";
import ResidentDocumentAccess from "./ResidentDocumentAccess";
import { isAxiosError } from "axios";

const CATEGORIES: DocumentCategory[] = ["BYLAWS", "MEETING_MINUTES", "CONTRACT", "OTHER"];
const FIELD = "mt-1 w-full rounded-xl border border-gray-300 bg-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A93]/30";
const BUTTON = "rounded-xl px-4 py-2.5 text-sm font-medium ring-1 ring-gray-200 disabled:opacity-50";

function serverMessage(error: unknown, fallback: string) {
  if (isAxiosError(error) && error.response?.data && typeof error.response.data === "object" && !(error.response.data instanceof Blob)) {
    const values = Object.values(error.response.data).flat();
    return values.map(String).join(" ") || fallback;
  }
  return fallback;
}

export default function DocumentsPage({ admin = false }: { admin?: boolean }) {
  return admin ? <DocumentLibrary admin /> : <ResidentDocumentAccess><DocumentLibrary admin={false} /></ResidentDocumentAccess>;
}

function DocumentLibrary({ admin }: { admin: boolean }) {
  const { t, language } = useLanguage();
  const [params] = useSearchParams();
  const targetDocument = params.get("document");
  const targetBuilding = params.get("building");
  const scrolledTarget = useRef<string | null>(null);
  const client = useQueryClient();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [building, setBuilding] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const filters = { search, category, building };
  const documents = useQuery({ queryKey: [DOCUMENTS_QUERY_KEY, filters], queryFn: () => listDocuments(filters),
    // Data freshness must not depend on optional bell delivery or SSE health.
    refetchInterval: 30_000, refetchOnWindowFocus: true });
  useEffect(() => {
    setSearch("");
    setCategory("");
    setBuilding(admin ? targetBuilding || "" : "");
  }, [targetDocument, targetBuilding, admin]);
  useEffect(() => {
    if (targetDocument && scrolledTarget.current !== targetDocument && documents.data?.some((item) => item.id === targetDocument)) {
      document.getElementById(`document-${targetDocument}`)?.scrollIntoView({ block: "center" });
      scrolledTarget.current = targetDocument;
    }
  }, [targetDocument, documents.data]);
  const buildings = useQuery({ queryKey: ["buildings", "mine"], queryFn: listBuildings, enabled: admin });
  const upload = useMutation({ mutationFn: uploadDocument, onSuccess: () => {
    void client.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY] });
    setShowUpload(false);
    setUploadError("");
  } });
  const visibility = useMutation({ mutationFn: ({ id, value }: { id: string; value: DocumentVisibility }) => changeDocumentVisibility(id, value),
    onSuccess: () => { void client.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY] }); } });
  const download = useMutation({ mutationFn: downloadDocument });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setUploadError("");
    const data = new FormData(event.currentTarget);
    const file = data.get("file");
    if (!(file instanceof File) || !file.size || file.size > 20 * 1024 * 1024) {
      setUploadError(t("m3.documents.sizeError"));
      return;
    }
    upload.mutate(data, { onError: (error) => setUploadError(serverMessage(error, t("m3.documents.uploadError"))) });
  }

  return <div className="space-y-5">
    <header className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white p-5 ring-1 ring-black/5">
      <div><h1 className="flex items-center gap-2 text-xl font-semibold"><FolderOpen className="h-6 w-6" />{t("m3.documents.title")}</h1>
        <p className="mt-1 text-sm text-gray-600">{t(admin ? "m3.documents.adminDescription" : "m3.documents.residentDescription")}</p></div>
      {admin && <button type="button" className={`${BUTTON} flex items-center gap-2 bg-[#1F3A93] text-white`}
        onClick={() => { setShowUpload(!showUpload); setUploadError(""); }}><Upload className="h-4 w-4" />{t(showUpload ? "common.cancel" : "m3.documents.upload")}</button>}
    </header>

    {admin && showUpload && <form onSubmit={submit} className="space-y-4 rounded-2xl bg-white p-5 ring-1 ring-black/5">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-sm font-medium">{t("m3.documents.building")}
          <select name="building" required defaultValue="" aria-label={t("m3.documents.building")} className={FIELD}>
            <option value="" disabled>{t("m3.documents.chooseBuilding")}</option>
            {buildings.data?.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select></label>
        <label className="text-sm font-medium">{t("m3.documents.name")}<input name="title" required maxLength={200} className={FIELD} /></label>
        <label className="text-sm font-medium">{t("m3.documents.category")}<select name="category" className={FIELD} defaultValue="OTHER">
          {CATEGORIES.map((item) => <option key={item} value={item}>{t(`m3.category.${item}`)}</option>)}
        </select></label>
        <label className="text-sm font-medium">{t("m3.documents.visibility")}<select name="visibility" className={FIELD} defaultValue="RESIDENTS">
          <option value="RESIDENTS">{t("m3.visibility.RESIDENTS")}</option><option value="ADMINS_ONLY">{t("m3.visibility.ADMINS_ONLY")}</option>
        </select></label>
      </div>
      <label className="block text-sm font-medium">{t("m3.documents.description")}<textarea name="description" maxLength={4000} rows={3} className={FIELD} /></label>
      <label className="block text-sm font-medium">{t("m3.documents.file")}<input type="file" name="file" required
        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.gif" className={FIELD} /></label>
      <p className="text-xs text-gray-500">{t("m3.documents.formats")}</p>
      {buildings.isError && <p role="alert" className="text-sm text-red-700">{t("m3.documents.buildingsError")}</p>}
      {uploadError && <p role="alert" className="text-sm text-red-700">{uploadError}</p>}
      <button type="submit" disabled={upload.isPending || buildings.isPending || buildings.isError} className={`${BUTTON} bg-[#1F3A93] text-white`}>{t(upload.isPending ? "m3.documents.uploading" : "m3.documents.upload")}</button>
    </form>}

    <div className="grid gap-3 rounded-2xl bg-white p-4 ring-1 ring-black/5 sm:grid-cols-3">
      <label className="text-sm">{t("m3.documents.search")}<input type="search" value={search} onChange={(event) => setSearch(event.target.value)} className={FIELD} /></label>
      <label className="text-sm">{t("m3.documents.category")}<select value={category} onChange={(event) => setCategory(event.target.value)} className={FIELD}>
        <option value="">{t("m3.documents.allCategories")}</option>{CATEGORIES.map((item) => <option key={item} value={item}>{t(`m3.category.${item}`)}</option>)}
      </select></label>
      {admin && <label className="text-sm">{t("m3.documents.building")}<select value={building} onChange={(event) => setBuilding(event.target.value)} className={FIELD}>
        <option value="">{t("m3.documents.allBuildings")}</option>{buildings.data?.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
      </select></label>}
    </div>

    {documents.isPending && <p role="status">{t("common.loading")}</p>}
    {documents.isError && <div role="alert" className="rounded-2xl bg-red-50 p-4 text-red-800"><p>{t("m3.documents.loadError")}</p>
      <button type="button" onClick={() => void documents.refetch()} className="mt-2 underline">{t("m3.retry")}</button></div>}
    {(download.isError || visibility.isError) && <p role="alert" className="text-sm text-red-700">{t(download.isError ? "m3.documents.downloadError" : "m3.documents.visibilityError")}</p>}
    {documents.data?.length === 0 && <div className="rounded-2xl bg-white p-10 text-center text-gray-600 ring-1 ring-black/5"><FileText className="mx-auto mb-3 h-9 w-9 text-gray-400" />{t("m3.documents.empty")}</div>}
    <div className="grid gap-4 sm:grid-cols-2">{documents.data?.map((item: CommunityDocument) => <article id={`document-${item.id}`} key={item.id} className={`min-w-0 rounded-2xl bg-white p-5 ring-1 ${item.id === targetDocument ? "ring-[#1F3A93]" : "ring-black/5"}`}>
      <h2 className="break-words text-lg font-semibold">{item.title}</h2>
      <p className="mt-1 text-xs text-gray-500">{item.building_name} · {t(`m3.category.${item.category}`)}</p>
      {item.description && <p className="mt-3 whitespace-pre-wrap break-words text-sm text-gray-700">{item.description}</p>}
      <p className="mt-3 break-all text-xs text-gray-500">{item.original_filename} · {(item.size_bytes / 1024 / 1024).toFixed(2)} MB</p>
      <p className="mt-1 text-xs text-gray-500">{item.uploaded_by_name} · {new Date(item.created_at).toLocaleDateString(language === "es" ? "es-VE" : "en-US")}</p>
      {admin && <label className="mt-4 block text-xs text-gray-600">{t("m3.documents.visibility")}<select className={FIELD} value={item.visibility} disabled={visibility.isPending}
        onChange={(event) => visibility.mutate({ id: item.id, value: event.target.value as DocumentVisibility })}>
        <option value="RESIDENTS">{t("m3.visibility.RESIDENTS")}</option><option value="ADMINS_ONLY">{t("m3.visibility.ADMINS_ONLY")}</option>
      </select></label>}
      <button type="button" disabled={download.isPending} onClick={() => download.mutate(item)} className={`${BUTTON} mt-4 flex items-center gap-2`}><Download className="h-4 w-4" />{t("m3.documents.download")}</button>
    </article>)}</div>
  </div>;
}
